// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TySupportStatus, TySupportTicket } from "@/types";
import { formatNumber } from "@/utils";
import StatCard from "@/UI/cards/StatCard";
import SupportTicketCard from "@/UI/cards/SupportTicketCard";
import ActionsBar, { type TyBarTab } from "@/UI/components/ActionsBar";
import ListPanel from "@/UI/components/ListPanel";
import Paginator from "@/UI/components/Paginator";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import SupportTicketPopup from "@/UI/popups/SupportTicketPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "open" | "pending" | "contacted" | "solved" | "all";

/** What each tab asks for. `open` is the work — everything nobody has closed out yet. */
const TAB_QUERY: Record<TyTab, Record<string, string>> = {
    open: {},
    pending: { status: "pending" },
    contacted: { status: "contacted" },
    solved: { status: "solved" },
    all: { status: "all" },
};

type TyStats = { pending: number; contacted: number; solved: number; total: number };

/**
 * Lozicore's support queue, and the simplest screen in this app on purpose.
 *
 * **One list, one popup, and nothing that edits itself.** The people working this are not technical:
 * there is no table, no inline cell editing and no bulk bar — a row opens a panel, the panel has a
 * status and a box to write in, and one button saves. The four figures at the top say how much work
 * there is; the tabs say which slice you are looking at.
 *
 * `open` leads because it is the only scope with anything owed in it. Nobody here creates a ticket —
 * a company writes it — and nothing deletes one, like everywhere else under `/admins`.
 */
export default function SupportScreen() {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);
    const requestClose = usePopup((state) => state.requestClose);
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const tab = (params.get("tab") as TyTab) || "open";
    const search = params.get("search") ?? "";
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);

    const list = useFetch<{ tickets: TySupportTicket[]; total: number; stats: TyStats }>("/support", {}, false);
    /** The one write the tiles do on their own: moving a ticket along its trail. */
    const move = useFetch("/support", {}, false);

    const load = () => list.invoke({ query: { ...TAB_QUERY[tab], search: search.trim() || undefined, page, limit: pageSize } });

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, search, page, pageSize]);

    useEffect(() => {
        if (!move.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [move.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        // Narrowing a list — or changing tab — puts you back at its start.
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const rows = list.data?.tickets ?? [];
    const stats = list.data?.stats;
    const tabs: TyBarTab[] = (Object.keys(TAB_QUERY) as TyTab[]).map((key) => ({ key, label: t.support.tabs[key] }));

    const moveStatus = (ticket: TySupportTicket, status: TySupportStatus) =>
        move.invoke({ method: "PATCH", path: `/support/${ticket._id}`, data: { status } });

    /** The person's own page, which is where a password is issued and the rest of their tickets are. */
    const openClient = (ticket: TySupportTicket) => {
        if (!ticket.client) return;
        requestClose("support-ticket");
        navigate(`/clients/${ticket.client._id}`);
    };

    /** One panel for the whole ticket — reading it and answering it are the same act. */
    const openTicket = (ticket: TySupportTicket) =>
        addComp({
            name: "support-ticket",
            Component: SupportTicketPopup,
            anim: "scale",
            details: {
                ticket,
                onStatus: (status: TySupportStatus) => moveStatus(ticket, status),
                onDone: load,
                onClient: ticket.client ? () => openClient(ticket) : undefined,
            },
        });

    return (
        <section className="list-screen">
            <ListPanel
                title={t.support.title}
                subtitle={t.support.subtitle.replace("{pending}", String(stats?.pending ?? 0)).replace("{total}", String(stats?.total ?? 0))}
                note={tab === "open" ? t.support.openHint : undefined}
            >
                <ActionsBar
                    tabs={tabs}
                    activeTab={tab}
                    onTab={(key) => apply({ tab: key === "open" ? null : key })}
                    isFilterOpen={isFiltering}
                    onFilterOpen={() => {
                        setDraftSearch(search);
                        setIsFiltering(true);
                    }}
                    hasFilters={!!search}
                    onFilterApply={() => {
                        setIsFiltering(false);
                        apply({ search: draftSearch.trim() || null });
                    }}
                    onFilterClear={() => {
                        setDraftSearch("");
                        setIsFiltering(false);
                        apply({ search: null });
                    }}
                    onFilterCancel={() => setIsFiltering(false)}
                    filterContent={
                        <FilterSearchInput value={draftSearch} onChange={setDraftSearch} label={t.common.search} placeholder={t.support.searchHint} />
                    }
                />
            </ListPanel>

            {/* How much work there is, over the whole queue rather than the page. */}
            <div className="stat-row">
                <StatCard icon="support" value={formatNumber(stats?.pending ?? 0)} label={t.support.statuses.pending} />
                <StatCard icon="refresh" value={formatNumber(stats?.contacted ?? 0)} label={t.support.statuses.contacted} />
                <StatCard icon="check" value={formatNumber(stats?.solved ?? 0)} label={t.support.statuses.solved} />
                <StatCard icon="stat" value={formatNumber(stats?.total ?? 0)} label={t.support.statTotal} />
            </div>

            {list.loading && !rows.length && <p className="screen-state">{t.common.loading}</p>}
            {(list.error || move.error) && <p className="screen-state screen-state--error">{list.error ?? move.error}</p>}
            {!list.loading && !list.error && !rows.length && <p className="screen-state">{t.support.empty}</p>}

            <div className="support-screen__grid">
                {rows.map((ticket) => (
                    <SupportTicketCard
                        key={ticket._id}
                        ticket={ticket}
                        onOpen={() => openTicket(ticket)}
                        onStatus={(status) => moveStatus(ticket, status)}
                        onReply={() => openTicket(ticket)}
                        isSaving={move.loading}
                    />
                ))}
            </div>

            <Paginator
                page={page}
                total={list.data?.total ?? 0}
                count={rows.length}
                onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
            />
        </section>
    );
}
